// THE ONE RENDERING CONSTRAINT: how small a tile can get before it stops
// saying anything (AG-2104).
//
// Sizes used to be decided by CHART TYPE, in four places that did not agree:
//
//   * `build_spec._grid_ref` — span 3 for a KPI, 12 for a table, 6 otherwise;
//     height short / tall / medium on the same test.
//   * `dashboard_layout` — `_WIDE` / `_NARROW` / `_KPI` / `_BANNER` sets and a
//     `_CHROME_DIMS` table giving `ranked_list` a literal 6x7.
//   * `theme.DENSITY` — a kpi / chrome / chart pixel floor per density word.
//   * every seed in `chart_seeds.py` — `height=340`, `height=320`, `height=260`.
//
// A chart's type is not a composition decision. "This is a bar chart" does not
// say how much of the page it should command; the design says that, and the
// container says what that comes to in pixels. But a type DOES say what has to
// fit inside the box — an axis needs room for tick labels before they collide, a
// table needs room for a header and a few rows, a single figure needs one line
// of type — and that is a rendering fact, true of every board, which is why it
// is stated once, here.
//
// Two rules hold this file honest:
//
//   * Nothing here returns a constant. Every number is a count — of characters,
//     of lines, of rows — multiplied by the BOARD'S OWN type scale and compared
//     against the MEASURED container. Change the density or the typeface and
//     every minimum moves with it.
//   * Nothing here is a preference. A floor says "below this the tile is
//     illegible", never "this is how tall a chart looks nice". A design that
//     asks for more gets more; a design that asks for less than legible is the
//     only case this overrides, and `regionMinHeight` documents that boundary.

import type { DashTheme } from './types';
import { roleLeading, roleSizeFrom, scaleRatio } from './typeRoles';

/** What a tile's form needs room for. Derived from what the chart DRAWS, never
 *  from a size table: the same descriptor answers for a bar chart and for any
 *  form added later that also plots against an axis. */
export interface ContentNeeds {
  /** Draws a plot area with tick labels along at least one edge. */
  axis: boolean;
  /** Lists rows with a header — a table, a ranked list. */
  rows: boolean;
  /** States one figure and its label; no plot area at all. */
  single: boolean;
  /** Draws a key mapping colour to series/category. */
  legend: boolean;
  /** Its marks are arranged around a centre, so width and height must be
   *  comparable or the shape degenerates (a donut in a 6:1 box is a line). */
  radial: boolean;
}

/**
 * A chart form → what it has to fit. One table, mirroring the backend's
 * `chart_types.VISUAL_ASSERTION`, which is already the engine's single semantic
 * classification of forms. Mirrored rather than imported for the same reason
 * `layout.ts` mirrors `layout_v2.py`: the backend authors, the browser draws,
 * and the browser is handed a `chart_type` string.
 *
 * Note what this does NOT contain: a width, a height, a span, or an aspect
 * ratio. It says what is inside the box; the caller says how big the box is.
 */
const ASSERTION: Record<string, string> = {
  kpi: 'single_value', metric: 'single_value', gauge: 'single_value',
  goal_progress: 'single_value', status_tile: 'single_value',
  segmented_progress: 'single_value', bullet: 'single_value',

  bar: 'comparison', column: 'comparison', grouped_bar: 'comparison',
  stacked_bar: 'comparison', lollipop: 'comparison', dot_plot: 'comparison',
  radar: 'comparison', pareto: 'comparison', marimekko: 'comparison',
  choropleth: 'comparison', ranked_list: 'comparison',

  line: 'trend', area: 'trend', stacked_area: 'trend', spline: 'trend',
  step: 'trend', control: 'trend', calendar_heatmap: 'trend', timeline: 'trend',
  gantt: 'trend', candlestick: 'trend', ohlc: 'trend', stock_volume: 'trend',

  pie: 'part_to_whole', donut: 'part_to_whole', treemap: 'part_to_whole',
  sunburst: 'part_to_whole', funnel: 'part_to_whole', pyramid: 'part_to_whole',
  waterfall: 'part_to_whole',

  scatter: 'relationship', bubble: 'relationship', hexbin: 'relationship',
  heatmap: 'relationship',

  histogram: 'distribution', box_plot: 'distribution', violin: 'distribution',
  density: 'distribution', ridgeline_plot: 'distribution',

  symbol_map: 'geography', flow_map: 'geography', world_map: 'geography',

  sankey: 'flow', network: 'flow', dendrogram: 'flow', org_chart: 'flow',
  flowchart: 'flow',

  table: 'record_listing', data_table: 'record_listing',
  insight_banner: 'narrative',
};

/** What this form asserts about its data, or '' for a form we do not know.
 *  Unknown is permissive: it is treated as an ordinary plot. */
/** Whether this chart form states ONE figure and labels it itself.
 *
 *  ⭐ THE ONE DEFINITION (AG-2203). There were three, and they disagreed:
 *  `theme.SELF_LABELED` and `PublicSharePage._isKpi` each carried their own
 *  four-name list (`kpi, metric, stat, status_tile`), while `ASSERTION` below —
 *  the table that classifies all ~60 forms — says `single_value` is
 *  `kpi, metric, gauge, goal_progress, status_tile, segmented_progress, bullet`.
 *  So `stat` was a KPI in two places and unknown to the classifier, and a gauge
 *  or a bullet was a KPI to the classifier and not to the other two: the same
 *  tile drew its header on the canvas and not in the export.
 *
 *  A form's meaning may shape what it shows. It may not mean three different
 *  things depending on which file is asking.
 */
export function isSelfLabelled(chartType?: string): boolean {
  return assertionOf(chartType) === 'single_value';
}

export function assertionOf(chartType?: string): string {
  return ASSERTION[String(chartType || '').trim().toLowerCase()] || '';
}

export function contentNeeds(chartType?: string): ContentNeeds {
  const a = assertionOf(chartType);
  return {
    single: a === 'single_value',
    rows: a === 'record_listing',
    radial: a === 'part_to_whole' || a === 'geography' || a === 'flow',
    axis: !['single_value', 'record_listing', 'narrative', 'part_to_whole', 'geography', 'flow'].includes(a),
    legend: ['part_to_whole', 'relationship', 'flow'].includes(a),
  };
}

// ── the counts, and why each one is what it is ───────────────────────────────
//
// These are the only bare numbers in this module, and every one is a count of
// something a reader can point at. None is a pixel, and none is a preference —
// a tile below any of them is unreadable rather than merely plain.

/* ⛔ `MIN_TICKS_ABREAST = 3`, `MIN_TICK_CHARS = 5` and `MIN_LABEL_CHARS = 8`
   WERE HERE (AG-2252). Three counts multiplied by an average character width to
   guess how much room text needs — when the text itself is in hand. A board's
   tiles have titles; a guess about eight characters of an unknown label is a
   worse answer than measuring the seven characters actually there.

   They were also the last place the chart's NAME decided a size: an axis form
   got 3x5 characters, a table 16, everything else 8. AG-2251 removed that from
   the height and left it standing in the width. */
/** The widest a card holding one figure and its label may be drawn, in
 *  characters of the board's own typeface. Past a measure of this order a line
 *  of type stops being scannable, and a stat card is much shorter than a line —
 *  so this is generous for it and still far under the 1,400px of empty white a
 *  live board once put beside "Share advancing". Published as
 *  `--dt-stat-measure` in `ch`, so it tracks the face and the size rather than
 *  freezing a pixel count the way `--dt-kpi-max: 380px` did. */
export const MAX_STAT_MEASURE_CH = 44;

/** A radial mark whose box is more than this many times as wide as it is tall
 *  has no room left for its own diameter. Not a preferred aspect — the point at
 *  which the shape stops being drawable in the shorter dimension. */
const MAX_RADIAL_ASPECT = 2.5;

/**
 * The board's own label size in pixels, read from the theme's density.
 *
 * `density` is the only type-scale control the design vocabulary has, and it is
 * the design's, not this file's: a compact board asks for tighter type and gets
 * proportionally smaller minimums, which is the whole reason a compact board can
 * hold more per row. The multipliers below are relative, so nothing here fixes
 * a size — they scale whatever the app's own base size turns out to be.
 */
const DENSITY_SCALE: Record<string, number> = { compact: 0.85, cozy: 1, spacious: 1.15 };

/** The blueprint's word for the themer's middle density. ONE alias table, here
 *  in the lowest module, so `theme.ts` and this file cannot disagree about it. */
const DENSITY_ALIAS: Record<string, string> = { comfortable: 'cozy' };

/** The density word a theme states, aliased, or the middle one when it states
 *  none. Every reader of `theme.density` goes through this. */
export function densityWord(theme?: DashTheme): string {
  const word = String(theme?.density || 'cozy').trim().toLowerCase();
  return DENSITY_ALIAS[word] ?? word;
}

/** The app's base UI text size. Read from the document so the board follows the
 *  application rather than a number in this file; falls back to the CSS default
 *  root size when there is no document (tests, SSR). */
export function rootFontPx(): number {
  if (typeof document === 'undefined' || !document.documentElement) return 16;
  const raw = getComputedStyle(document.documentElement).fontSize;
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 16;
}

/** The density's effect on type. Exported so `typography.ts` scales the ROLES
 *  by the same factor this module scales its minimums by — a compact board's
 *  smaller text and its tighter floors are one decision, not two. */
export function densityTypeScale(theme?: DashTheme): number {
  return DENSITY_SCALE[densityWord(theme)] ?? 1;
}

/** One line of the board's small label type, in pixels. Everything below is
 *  counted in these. */
export function labelLinePx(theme?: DashTheme): number {
  // 0.75em is the app's own small-label step (`text-xs`); the LEADING is the
  // `label` role's, owned by `typography` and published as `--dt-leading-label`
  // — this used to be a bare 1.4 here, which made a line box measured for a
  // floor and a line box rendered by CSS two different answers (AG-2174).
  return rootFontPx() * 0.75 * roleLeading('label') * densityTypeScale(theme);
}

/** The typeface the board is set in, for measuring in. Read off the document so
 *  a board set in Fraunces is measured in Fraunces; the app's own stack when the
 *  design named none. */
function fontFamilyOf(theme?: DashTheme): string {
  const named = (theme as { fontFamily?: unknown } | undefined)?.fontFamily;
  if (typeof named === 'string' && named.trim()) return named.trim();
  if (typeof document === 'undefined' || !document.documentElement) return 'sans-serif';
  return getComputedStyle(document.documentElement).fontFamily || 'sans-serif';
}

let measuringCtx: CanvasRenderingContext2D | null | undefined;

/** A 2D context to measure in, made once. `undefined` means not yet tried,
 *  `null` means this environment has no canvas. */
function ctxFor(sizePx: number, family: string): CanvasRenderingContext2D | null {
  if (measuringCtx === undefined) {
    measuringCtx = typeof document === 'undefined'
      ? null
      : document.createElement('canvas').getContext('2d');
  }
  if (!measuringCtx) return null;
  measuringCtx.font = `${sizePx}px ${family}`;
  return measuringCtx;
}

/**
 * How wide this text actually is, at the board's own label size and face.
 *
 * ⭐ MEASURED, NOT COUNTED (AG-2252). This replaces `labelCharPx`, an average
 * advance width of 0.55em multiplied by a guessed character count. The average
 * is a real typographic property, and it is still the wrong tool when the string
 * is in hand: "Units" and "Wednesday" are both words, and one is twice the other.
 *
 * ⚠️ The 0.55 survives as the NO-DOCUMENT fallback only — a unit test or an SSR
 * pass has no canvas to measure with, and a rough answer there is better than
 * none. Nothing in a browser reaches it.
 */
export function textWidthPx(text: string, theme?: DashTheme): number {
  const size = rootFontPx() * 0.75 * densityTypeScale(theme);
  const ctx = ctxFor(size, fontFamilyOf(theme));
  if (!ctx) return String(text).length * size * 0.55;
  return ctx.measureText(String(text)).width;
}

/** The card's own chrome — its padding, and the title and question above the
 *  plot — in pixels. Drawn from the same density the rest of the card uses. */
/** The board's gutter, read without importing `theme` — which imports this file. */
function gapPx(theme?: DashTheme): number {
  const stated = (theme as { tileGap?: unknown } | undefined)?.tileGap;
  if (typeof stated === 'number' && Number.isFinite(stated) && stated >= 0) return stated;
  return Math.round(rootFontPx() * 0.75 * densityTypeScale(theme));
}

export function chromePx(theme?: DashTheme, headerless = false): number {
  const line = labelLinePx(theme);
  // Padding top and bottom, plus a title line and the question under it when the
  // card draws a header at all.
  return line * (headerless ? 1 : 3);
}

/**
 * The narrowest this tile can be drawn and still be read.
 *
 * Replaces `MAX_GRID_COLUMNS = 6`, whose comment gave the real reason — "six
 * columns at 1440px is ~220px a tile, which is about where a chart stops being
 * readable" — and then froze the conclusion instead of the reasoning. Six was
 * only right at that width, in that typeface, at that density.
 */
export function minTileWidthPx(_chartType?: string, theme?: DashTheme, text?: string): number {
  // ⭐ FORM-INDEPENDENT AND MEASURED (AG-2252). This branched on the chart's
  // classified form and multiplied a character count by an average advance
  // width: 3x5 characters for an axis, 16 for a table, 8 for everything else.
  // Both halves were guesses — about what the tile holds, and about how wide
  // that is — when the tile's own words are in hand.
  //
  // ⚠️ `chartType` is accepted and IGNORED, the same way `minTileHeightPx` took
  // it after AG-2251, so the callers need not all change at once.
  const pad = labelLinePx(theme); // left + right card padding
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  // WITH NOTHING TO MEASURE, THE FLOOR IS THE CHROME AND NOTHING ELSE. A tile
  // whose text this caller does not know has no width requirement the renderer
  // can honestly state — and inventing one is what let a legibility guess bound
  // a composition. The bound that matters is still there: `gridColumns` never
  // gives a region more columns than it has members.
  // ── THE FLOOR UNDER EVERYTHING: not thinner than it is tall ───────────────
  //
  // ⚠️ WHY A FLOOR IS STILL NEEDED, and what it may NOT be. The text this can
  // measure is the tile's TITLE, and a chart's axis ticks and category labels
  // are routinely wider than any word in its title — but they arrive with the
  // DATA, long after the columns are decided, so nothing here can see them.
  // Measured live at 390px with the title alone behind it: six columns were
  // claimed where two fit, and a bar chart drew 4.4px past its own right edge.
  //
  // ⇒ So the floor is the room the BOARD'S OWN TYPE already requires vertically
  // (`minTileHeightPx`, itself form-independent since AG-2251). A tile thinner
  // than it is tall is a sliver whatever it holds. This mentions no chart type
  // and counts no characters: it moves with the density and the type scale
  // because it IS the board's type, and it is a renderer CONSTRAINT — a floor
  // under a tile — rather than a decision about how the board should look.
  const floor = minTileHeightPx(undefined, theme);
  if (!words.length) return floor;
  // The longest UNBREAKABLE run, because a title wraps: a tile narrower than its
  // longest word cannot draw it, while a tile narrower than the whole phrase
  // simply sets it on two lines.
  const measured = Math.max(...words.map((word) => textWidthPx(word, theme))) + pad;
  return Math.max(measured, floor);
}

/**
 * The shortest this tile can be drawn and still be read.
 *
 * Replaces `DENSITY.{kpi, chrome, chart}` — 132/80/252, 160/96/300, 184/112/344
 * — where the three numbers per density were a chart-type table wearing a
 * density's clothes.
 */
/* ⛔ `minPlotHeightPx` WAS HERE (AG-2205). It reserved room for the drawing by
   asking what KIND of chart it was: five "plot bands" for anything with an axis,
   three rows for a table, two doubled lines for a single value. Every one was a
   count standing in for a measurement it never performed, and it is what gave an
   eight-category bar chart a 440x150 box.

   Two things replaced it, because it was answering two questions at once.
   `typography.undrawnFloorPx` answers "how much room before anything is drawn",
   from the board's own type rather than from the chart's name.
   `chartRuntime.requiredHeightPx` answers "how much room does this drawing need",
   by measuring the drawing. */

export function minTileHeightPx(_chartType?: string, theme?: DashTheme, headerless = false): number {
  // ⭐ FORM-INDEPENDENT (AG-2205). This used to branch on the chart's classified
  // form and multiply a generic count by a line height. It answers the same
  // question `undrawnFloorPx` does — how much room a tile needs before anything
  // is drawn in it — so it answers it the same way: room for the largest text a
  // tile can hold, from the BOARD'S OWN TYPE, plus the label under it.
  //
  // ⚠️ `chartType` is accepted and IGNORED so the remaining callers need not all
  // change at once. It goes from the signature when the last one stops passing it.
  const base = rootFontPx() * 0.8125 * densityTypeScale(theme);
  const ratio = scaleRatio((theme as { typeScale?: string } | undefined)?.typeScale);
  // The figure, the label under it, and the AIR BETWEEN THEM. The gap is the
  // board's own, so the three move together: a spacious board's floor is taller
  // because a spacious board sets those two lines further apart. Leaving the gap
  // out under-counted by exactly the space between a headline number and the
  // words saying what it is, which is not optional (AG-2205).
  const metric = roleSizeFrom('metric', base, ratio) * roleLeading('metric');
  const between = gapPx(theme) / 2;
  return chromePx(theme, headerless) + metric + between + labelLinePx(theme);
}

/**
 * The tallest a radial tile may be drawn at this width before its own shape
 * stops fitting, or `null` for a form with no such constraint.
 *
 * This is the "chart aspect requirement" the layout engine needs: a donut in a
 * 6:1 box draws a line. It bounds the box against ITSELF rather than against a
 * number — there is no preferred height here, only a width past which more
 * height buys nothing and a width below which the mark cannot close.
 */
export function radialWidthCapPx(chartType: string | undefined, heightPx: number): number | null {
  return contentNeeds(chartType).radial && heightPx > 0 ? heightPx * MAX_RADIAL_ASPECT : null;
}

/**
 * How many columns a container of this width can hold before its tiles stop
 * being readable — the container's answer, not a constant.
 *
 * `gap` is the board's own gutter, so a spacious board fits fewer columns than a
 * compact one at the same width, which is what asking for air means.
 */
export function columnsThatFit(
  containerPx: number, gapPx: number, chartType?: string, theme?: DashTheme, text?: string,
): number {
  const min = minTileWidthPx(chartType, theme, text);
  if (!(containerPx > 0) || !(min > 0)) return 1;
  // n columns need n tiles plus (n-1) gutters: n ≤ (W + gap) / (min + gap).
  return Math.max(1, Math.floor((containerPx + gapPx) / (min + gapPx)));
}
